import React, { useState, useEffect, useRef } from 'react';
import Pagination from "react-pagination-list";
import { fetcher } from '../fetchers';
import { isEqual, round } from 'lodash';

export const DEFAULT_DISCIPLINE_IDS = {
  open: 13,
  youth: 13,
  challenger: 13,
  pro: 13,
  all: 0,
};

const usePreviousValue = value => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const LeaderboardsResults = ({ segments, selectedType, selectedSegment, setLoading, typesList }) => {
  const [allLeaderboards, setAllLeaderboards] = useState([]);
  const [segmentLeaderboards, setSegmentLeaderboards] = useState([]);
  const [selectedLeaderboard, setSelectedLeaderboard] = useState();
  const prevTypeValue = usePreviousValue(selectedType);
  const prevSegmentValue = usePreviousValue(selectedSegment);

  const selectDefaultLeaderboard = (segLeaderboards)=> {
    const leaderboardId = DEFAULT_DISCIPLINE_IDS[selectedType];
    return segLeaderboards.filter((leaderboard, i) => {
      const hasDefaultDisciplineResults = segLeaderboards.some(dis => dis.id === leaderboardId) && leaderboard.id === leaderboardId;
      const useFirstLeaderboardDiscipline = !segLeaderboards.some(dis => dis.id === leaderboardId) && i === 0;
      return hasDefaultDisciplineResults || useFirstLeaderboardDiscipline;
    })[0];
  };

  const selectDiscipline = (segLeaderboards)=> {
    const selectedboard = selectDefaultLeaderboard(segLeaderboards);
    setSelectedLeaderboard(selectedboard);
    setLoading(false);
  };

  const changeSelectedDiscipline = (id)=> {
    const selectedboard = segmentLeaderboards.filter(leaderboard => leaderboard.id === parseInt(id))[0];
    setSelectedLeaderboard(selectedboard);
  };

  const getSegmentLeaderboards = async (allDisciplines) => {
    const isAllAround = selectedType === 'all';
    const segLeaderboards = isAllAround
      ? [{
          id: 0, 
          name: 'All Around', 
          sortOrder: 0, 
          leaderboard: []
        }]
      : [];

    const allAroundLeaderboard = segLeaderboards.find((elem) => elem.name === 'All Around');

    const mergeAllAroundResults = (leaderboards) => {
      const mergedLeaderboards = allAroundLeaderboard.leaderboard.reduce((acc, athlete, i) => {
        const { ERAUID, FullName, points, place, money, disciplineId,} = athlete;
        const found = acc.find(elem => elem.ERAUID == ERAUID);
        const hasCompetionsPoints = !!place && !!money;

        athlete.hasAllAround = false;
        athlete.hasCompetionsPoints = hasCompetionsPoints;
        athlete.disciplines = [];

        if (found) {
          const multipleDisciplines = found.disciplineId !== disciplineId;
          const hasAllAround = found.hasAllAround;
          const foundDisciplinePoints = found.disciplines.find(elem => elem.disciplineId == disciplineId);

          if (foundDisciplinePoints) {
            foundDisciplinePoints.points += points;
          } else if (hasCompetionsPoints && points >= 15) {
            found.disciplines.push({disciplineId, points});
          }

          found.hasAllAround = hasAllAround || (multipleDisciplines && (found.hasCompetionsPoints || hasCompetionsPoints));
        } else if (hasCompetionsPoints) {
          athlete.disciplines.push({disciplineId, points});
          acc.push({...athlete});
        }

        return acc;
      }, [])
        .reduce((acc, athlete) => {
          const athleteDisciplines = athlete.disciplines;
          athlete.points = 0;
          if (athleteDisciplines.length > 1) {
            athleteDisciplines.map((athleteDiscipline) => {
                athlete.points += athleteDiscipline.points
            });
          }
          if (athlete.points >= 15) acc.push(athlete);
          return acc;
        }, [])
        .sort((a, b) => b.points - a.points);

      return [{
        id: 0, 
        name: 'All Around', 
        sortOrder: 0, 
        leaderboard: mergedLeaderboards
      }];
    }

    const getDisciplineLeaderboards = (disciplineLeaderboards) => {
      disciplineLeaderboards.map((leaderboard, index) => {
        const hasLeaderboard = leaderboard.data.length;
        const discipline = allDisciplines[index];
        if (!!hasLeaderboard) { 
          if (isAllAround) {
            allAroundLeaderboard.leaderboard.push(...leaderboard.data);
          } else {
            segLeaderboards.push({ 
              id: discipline.id, 
              name: discipline.name, 
              sortOrder: discipline.SortOrder, 
              leaderboard: leaderboard.data 
            });
          }
        };
        if (allDisciplines.length === index + 1) {
          const sortedSegLeaderboards = isAllAround 
            ? mergeAllAroundResults(segLeaderboards) 
            : segLeaderboards.sort((a, b) => a.sortOrder - b.sortOrder);
          setSegmentLeaderboards(sortedSegLeaderboards);
          selectDiscipline(sortedSegLeaderboards);
        }
      })
    };
    const disciplineLeaderboards = await Promise.all(allDisciplines.map(async (discipline, index) => {
      const leaderboardAPIPath = selectedSegment.SegmentUID.length > 1 
        ? `/v1/contestants/leaderboard/public/?type=${ selectedType }&segment=${ selectedSegment.SegmentUID }&discipline=${ discipline.id }&startDate=${selectedSegment.StartDate}&endDate=${selectedSegment.EndDate}`
        : `/v1/contestants/leaderboard/public/?type=${ selectedType }&segment=${ selectedSegment.SegmentUID }&discipline=${ discipline.id }`
      return await fetcher( `get`, leaderboardAPIPath);
    }));

    getDisciplineLeaderboards(disciplineLeaderboards);
  };

  const getAllDisciplines = () => {
    setLoading(true);
    const handleSuccess = (res) => getSegmentLeaderboards(res.data);
    fetcher(`get`, `/v1/disciplines/public?nominatableOnly=true&segment=${ selectedSegment.SegmentUID }`)
      .then(handleSuccess, handleError)
      .catch(handleError);
  };

  const handleError = (error) => { throw error; };

  useEffect(() => {
    const propsChanged = 
      !!selectedType && 
      !!selectedSegment &&
      (!isEqual(prevTypeValue, selectedType) || !isEqual(prevSegmentValue, selectedSegment));
    if (propsChanged) getAllDisciplines();

    if(selectedSegment.Name.includes('VRQ Bonus 2024')) {
      setSelectedLeaderboard(undefined);
      setSegmentLeaderboards([]);
      setLoading(false);
    }
  },[selectedType, selectedSegment]);

  //For simple reporting purposes
  function convertToCSV(arr) { 
    const filteredArr = arr
    // .slice(0,16)
    .reduce((acc, result) => {
      acc.push({fullname: result.FullName, points: result.points, position: result.position, });
      return acc;
    },[]
    )
    const array = [Object.keys(filteredArr[0])].concat(filteredArr)
  
    return array.map(it => {
      return Object.values(it).toString()
    }).join('\n')
  }

  let breadCrumpText = 'List is not available';
  if (selectedLeaderboard) {
    breadCrumpText = selectedSegment.isGroupedSegment 
      ? `${selectedType} / ${selectedSegment.Name} / ${selectedLeaderboard && selectedLeaderboard.name}`
      : `${selectedType} / Segment: ${selectedSegment.ShortName} / ${selectedLeaderboard && selectedLeaderboard.name}`
  }
  else {
    if (selectedSegment.ShortName.indexOf('DY') == 0) 
      breadCrumpText = <span className='lowerCaseHeader'>As of June 2022, the Division Youth Leaderboard can be viewed at DY.RODEO. Click <a className='headerLink' href="https://dy.rodeo/leaderboard/" title="https://dy.rodeo/leaderboard/" >HERE</a> to view.</span>
    if (selectedSegment.Name.includes('VRQ Bonus 2024')) 
      breadCrumpText = <span className='lowerCaseHeader'>
        <a className='headerLink' href="https://wcrarodeo.com/wp-content/uploads/RRVRQLeaderboard2025.pdf" title="https://wcrarodeo.com/wp-content/uploads/RRVRQLeaderboard2025.pdf" target='_blank' >
          Final 2024 Reliance Ranches VRQ Points Champion Standings
        </a>
        </span>

  }
  
  // remove ladyes goat tying from disciplines when rr ranches
  const filteredDisciplines = selectedSegment.Name.includes('VRQ Bonus 2024') ? segmentLeaderboards.filter(sl => sl.id !== 187) : segmentLeaderboards;
  return (
    <div id="leaderboard-details">
      <h4 className="leaderboard-breadcrumb">{breadCrumpText}</h4>
      <div id="discipline-selection">
        {filteredDisciplines && filteredDisciplines.map((leaderboard) => (
          <button
            className="discipline-option"
            style={{color: (selectedLeaderboard && (leaderboard.id == selectedLeaderboard.id) ? 'rgba(0,0,0,1)' : 'rgba(0,0,0,0.3)')}}
            value={leaderboard.id}
            onClick={(e) => changeSelectedDiscipline(e.target.value)} >
            {leaderboard.name}
          </button>
        ))}
      </div>
      <div id="segment-leadboard">
        <div className="leaderboardRow">
          {segmentLeaderboards && segmentLeaderboards.map((leaderboards) =>  { 
            const filteredLeaderboard = leaderboards.leaderboard.filter(l => (selectedSegment.isGroupedSegment && l.points > 200) || !selectedSegment.isGroupedSegment)
            return (
            <div
              className="columnContainer"
              key={leaderboards.id}
              style={{display: (selectedLeaderboard && leaderboards.id == selectedLeaderboard.id ? 'block' : 'none')}} >
              <div className="leaderboard-column">
                <ol id="discipline.class" className="rendered-list">
                  { leaderboards && filteredLeaderboard && (
                    <Pagination
                      id="leaderboard-list"
                      data={filteredLeaderboard}
                      pageSize={16}
                      renderItem={(athlete, key) => {
                        const rank = filteredLeaderboard.findIndex(li => li.ERAUID === athlete.ERAUID)+1;
                        const style = rank === athlete.QualifyingCutOff && leaderboards.name !== 'All Around' ? {borderBottom: "4px dashed #404040"} : {}
                        return (
                        <li key={key} className="athlete" style={style}>
                          <span className="rendered-list-rank">{rank}</span>
                          <span className="rendered-list-name">{athlete.FullName}</span>
                          <span className="rendered-list-number">{round(athlete.points, 3)}</span>
                        </li>
                      )
                      }}
                    />
                  )}
                </ol>
                { selectedLeaderboard?.leaderboard[0]?.QualifyingCutOff && leaderboards.name !== 'All Around' && (
                    <div>
                      <div style={{borderBottom: "4px dashed #404040", width: "50px", display: "inline-block", marginRight: "10px"}}>{` `}</div>
                      <span>Denotes qualified position cutoff for event</span>
                    </div>
                )}
              </div>
            </div>
          )})}
        </div>
      </div>
    </div>
  );
};
